
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { ChevronDown } from 'lucide-react';

const Hero = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const [isLoaded, setIsLoaded] = useState(false);

  // Define SEO-optimized keywords for the hero title based on language
  const heroTitleWords = currentLanguage === 'de' ? 
    ["KI-basierte", "Videomarketing", "Lösungen", "für", "Ihr", "Business"] : 
    t.heroTitle.split(' ');

  useEffect(() => {
    // Simulate loading time for animation entrance
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  const scrollToServices = (e: React.MouseEvent) => {
    e.preventDefault();
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative w-full h-screen overflow-hidden" aria-labelledby="hero-heading">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-[#1A1F2C]/60 z-10"></div>
        
        {/* Video container - absolute positioning to fill entire section */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <iframe 
            allow="fullscreen;autoplay" 
            allowFullScreen 
            src="https://streamable.com/e/dpk9yd?autoplay=1&muted=1&nocontrols=1&loop=1" 
            width="100%" 
            height="100%" 
            style={{
              border: "none", 
              width: "100%", 
              height: "100%", 
              position: "absolute", 
              left: "0px", 
              top: "0px", 
              overflow: "hidden",
              pointerEvents: "none",
              objectFit: "cover"
            }}
            title="Background Video"
            aria-hidden="true"
          ></iframe>
        </div>
        
        {/* Neural line pattern overlay */}
        <div className="absolute inset-0 z-11 opacity-20 pointer-events-none">
          <div className="absolute inset-0 bg-[length:50px_50px] animate-neural-light" 
               style={{backgroundImage: "radial-gradient(circle at 25px 25px, rgba(139, 92, 246, 0.5) 2px, transparent 0)"}}></div>
        </div>
      </div>

      {/* Content - Moved to the bottom left corner for better video visibility */}
      <div className="container mx-auto px-4 h-full flex items-end pb-16 relative z-20">
        <div className={`max-w-xl ${isLoaded ? 'animate-scale-in' : 'opacity-0'} transition-all duration-500 bg-black/30 backdrop-blur-sm p-8 rounded-lg border border-purple-500/20`}>
          <div className="overflow-hidden mb-6">
            <h1 id="hero-heading" className="text-white text-4xl md:text-5xl lg:text-7xl font-bold leading-tight tracking-tight font-future">
              {heroTitleWords.map((word, index) => (
                <div 
                  key={index} 
                  className="block micro-glitch mb-1" 
                  style={{ 
                    animationDelay: `${index * 0.1}s`,
                    textShadow: '0 0 8px rgba(139, 92, 246, 0.5)'
                  }}
                >
                  {word}
                </div>
              ))}
            </h1>
          </div>
          <p className="text-gray-200 text-lg md:text-xl mb-8 leading-relaxed tracking-wide overflow-hidden">
            <span className="block animate-fade-in" style={{ animationDelay: '0.5s' }}>
              {t.heroSubtitle}
            </span>
          </p>
          <div className="flex flex-col sm:flex-row gap-5 animate-fade-in" style={{ animationDelay: '0.7s' }}>
            <Link to={`/${currentLanguage}/kontakt`} className="neo-button text-center glow-effect relative overflow-hidden group">
              <span className="relative z-10">{t.contactUs}</span>
              <span className="absolute inset-0 bg-gradient-to-r from-purple-800/20 to-cyan-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
            </Link>
            <Link to={`/${currentLanguage}/warum-future-level`} className="neo-button-outline text-center pulse-border relative overflow-hidden group">
              <span className="relative z-10">{t.learnMore}</span>
              <span className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-500 to-transparent transform translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500"></span>
            </Link>
          </div>
        </div>
      </div>

      {/* Enhanced scroll indicator with improved positioning and animation */}
      <a 
        href="#services" 
        onClick={scrollToServices}
        className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 w-12 h-12 
                   flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm 
                   border border-purple-500/40 hover:border-purple-500/80 
                   transition-all duration-300 cursor-pointer group animate-fade-in"
        style={{ animationDelay: '1.2s' }}
        aria-label={currentLanguage === 'de' ? "Zu unseren Dienstleistungen scrollen" :
                  currentLanguage === 'fr' ? "Faites défiler jusqu'à nos services" :
                  currentLanguage === 'it' ? "Scorri fino ai nostri servizi" : 
                  "Scroll to our services"}
      >
        <ChevronDown 
          className="w-6 h-6 text-purple-400 group-hover:text-purple-300 transition-colors"
          style={{ animation: 'bounce 2s infinite' }}
          aria-hidden="true"
        />
      </a>
      
      {/* Tech grid decoration in corner */}
      <div className="absolute bottom-0 right-0 w-32 h-32 pointer-events-none opacity-30 z-10">
        <div className="w-full h-full bg-gradient-to-bl from-purple-500/20 to-transparent"></div>
        <div className="absolute inset-0 grid grid-cols-5 grid-rows-5">
          {Array(25).fill(0).map((_, i) => (
            <div 
              key={i} 
              className="border-[0.5px] border-purple-500/30"
              style={{
                opacity: Math.random() * 0.5 + 0.3,
                animation: `pulse ${Math.random() * 3 + 2}s infinite`
              }}
              aria-hidden="true"
            ></div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
